import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Snackbar,
  Skeleton,
  InputAdornment,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";
import { useCart } from "../store/CartContext";
import CategoryTabs from "../components/menu/CategoryTabs";
import MenuCard from "../components/menu/MenuCard";
import OptionsDialog from "../components/menu/OptionsDialog";
import FloatingCartButton from "../components/common/FloatingCartButton";
import LastOrderSnackbar from "../components/common/LastOrderSnackbar";
import useOrderingToggle from "../hooks/useOrderingToggle";
import useMenuData from "../hooks/useMenuData";
import useScrollThreshold from "../hooks/useScrollThreshold";
import useLastOrderBanner from "../hooks/useLastOrderBanner";
import ThaiPaperBackground from "../components/common/ThaiPaperBackground";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function MenuPage() {
  const navigate = useNavigate();
  const { addToCart, cartItems, removeAllById } = useCart();

  const isOrderingEnabled = useOrderingToggle();
  const { menuCategories, loading } = useMenuData();
  const showCartButton = useScrollThreshold(300);

  const [activeCategory, setActiveCategory] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("Added to cart!");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [bargainModalOpen, setBargainModalOpen] = useState(false);
  const [pendingBargain, setPendingBargain] = useState(null);

  const { lastOrderId, bannerOpen, dismissBanner, viewLastOrder } =
    useLastOrderBanner(navigate);

  const BARGAIN_KEYWORDS = ["mea bargain bag", "thai bargain bag", "bargain bag"];

  const LAST_BARGAIN_HOUR = 19;
  const LAST_BARGAIN_MINUTE = 30;

  const PICKUP_WINDOW_DEFAULT = "8:00 PM – 9:00 PM";
  const PICKUP_WINDOW_FRI_SAT = "8:00 PM – 9:30 PM (Fri/Sat)";

  const isBargainBagItem = (menuItem) => {
    const name = (menuItem?.name || "").toLowerCase();
    return BARGAIN_KEYWORDS.some((k) => name.includes(k));
  };

  const isBefore730pm = () => {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    return h < LAST_BARGAIN_HOUR || (h === LAST_BARGAIN_HOUR && m < LAST_BARGAIN_MINUTE);
  };

  const cartHasBargainBag = useMemo(() => {
    return cartItems.some((ci) =>
      BARGAIN_KEYWORDS.some((k) => (ci.name || "").toLowerCase().includes(k))
    );
  }, [cartItems]);

  const cartHasOtherItems = useMemo(() => {
    return cartItems.some(
      (ci) =>
        !BARGAIN_KEYWORDS.some((k) => (ci.name || "").toLowerCase().includes(k))
    );
  }, [cartItems]);

  const bargainBagAllowedNow = isOrderingEnabled && isBefore730pm();

  const pickupWindowText = useMemo(() => {
    const d = new Date();
    const day = d.getDay();
    const isFriOrSat = day === 5 || day === 6;
    return isFriOrSat ? PICKUP_WINDOW_FRI_SAT : PICKUP_WINDOW_DEFAULT;
  }, []);

  const isActive = (menuItem) => {
    return (
      menuItem?.isActive === true ||
      menuItem?.isActive === 1 ||
      String(menuItem?.isActive).toLowerCase() === "true"
    );
  };

  const filteredItems = useMemo(() => {
    const items = menuCategories?.[activeCategory]?.menuItems || [];
    const q = searchTerm.toLowerCase();

    return items.filter(
      (item) =>
        (item.name || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q)
    );
  }, [menuCategories, activeCategory, searchTerm]);

  const openSnack = (message, severity = "success") => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const SEPARATE_ORDER_MSG =
    "Mea Bargain Bag must be a separate order (no other items can be added). Please checkout or clear your cart first.";

  const addMenuItemToCart = (menuItem, qty = 1, overridePrice = null) => {
    const priceToUse =
      overridePrice != null ? overridePrice : Number(menuItem.basePrice || 0);

    if (priceToUse > 0) {
      addToCart({
        id: menuItem.menuItemId,
        name: menuItem.name,
        description: menuItem.description,
        price: priceToUse,
        image: menuItem.imageUrl,
        images: menuItem.images || [],
        qty,
      });
    }
  };

  const handleAddToCartClick = (menuItem, qty = 1) => {
    if (!isActive(menuItem)) return;

    const isBargain = isBargainBagItem(menuItem);

    if (isBargain && !bargainBagAllowedNow) {
      openSnack(
        "Mea Bargain Bag is only available while we’re open and before 7:30 PM.",
        "warning"
      );
      return;
    }

    if (!isBargain && cartHasBargainBag) {
      openSnack(SEPARATE_ORDER_MSG, "warning");
      return;
    }

    if (isBargain && cartHasOtherItems) {
      openSnack(SEPARATE_ORDER_MSG, "warning");
      return;
    }

    if (isBargain) {
      setPendingBargain({ menuItem, qty });
      setBargainModalOpen(true);
      return;
    }

    if (menuItem.options?.length) {
      setSelectedItem({ ...menuItem, _qty: qty });
      setPickerOpen(true);
      return;
    }

    addMenuItemToCart(menuItem, qty);
    openSnack("Added to cart!", "success");
  };

  const handleOptionsConfirm = ({ selections, finalPrice, quantity }) => {
    const selectedIsBargain = isBargainBagItem(selectedItem);

    if (selectedIsBargain && !bargainBagAllowedNow) {
      openSnack(
        "Mea Bargain Bag is only available while we’re open and before 7:30 PM.",
        "warning"
      );
      setPickerOpen(false);
      setSelectedItem(null);
      return;
    }

    if (!selectedIsBargain && cartHasBargainBag) {
      openSnack(SEPARATE_ORDER_MSG, "warning");
      setPickerOpen(false);
      setSelectedItem(null);
      return;
    }

    if (selectedIsBargain && cartHasOtherItems) {
      openSnack(SEPARATE_ORDER_MSG, "warning");
      setPickerOpen(false);
      setSelectedItem(null);
      return;
    }

    addToCart({
      id: selectedItem.menuItemId,
      name: selectedItem.name,
      description: selectedItem.description,
      price: finalPrice,
      image: selectedItem.imageUrl,
      images: selectedItem.images || [],
      qty: quantity ?? selectedItem._qty ?? 1,
      options: selections,
    });

    setPickerOpen(false);
    setSelectedItem(null);
    openSnack("Added to cart!", "success");
  };

  const handleBargainAccept = () => {
    if (!pendingBargain?.menuItem) {
      setBargainModalOpen(false);
      return;
    }

    const { menuItem, qty } = pendingBargain;

    if (menuItem.options?.length) {
      setSelectedItem({ ...menuItem, _qty: qty });
      setPickerOpen(true);
      setBargainModalOpen(false);
      setPendingBargain(null);
      return;
    }

    addMenuItemToCart(menuItem, qty);
    openSnack("Bargain Bag added — see you at pickup time 😄", "success");

    setBargainModalOpen(false);
    setPendingBargain(null);
  };

  const handleBargainDecline = () => {
    const id = pendingBargain?.menuItem?.menuItemId;
    if (id != null) removeAllById(id);
    setBargainModalOpen(false);
    setPendingBargain(null);
  };

  return (
    <ThaiPaperBackground>
      <Box sx={{ px: 2, py: { xs: 2, md: 3 } }}>
        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: 700, letterSpacing: 0.3, mb: 1 }}
        >
          Menu
        </Typography>

        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Dine with us or order online — crafted fresh, Thai-style.
        </Typography>

        {!isOrderingEnabled ? (
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              border: "1px solid",
              borderColor: "divider",
              backdropFilter: "blur(6px)",
              bgcolor: "rgba(255,255,255,0.76)",
            }}
          >
            <Typography variant="h6" sx={{ color: "error.main" }}>
              We are currently closed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Online ordering is not available right now.
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backdropFilter: "blur(10px)",
                bgcolor: "rgba(255,255,255,0.85)",
                border: "1px solid",
                borderColor: "rgba(0,0,0,0.06)",
                borderRadius: 2,
                p: 1.5,
                mb: 2,
              }}
            >
              {cartHasBargainBag && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    mb: 1.25,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.10)",
                    bgcolor: "rgba(255,245,230,0.95)",
                  }}
                >
                  <Typography sx={{ fontWeight: 900, mb: 0.25 }}>
                    🥡 Mea Bargain Bag is in your cart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This item must be purchased as a <b>separate order</b>. Checkout
                    to continue, or clear your cart to add other menu items.
                    <br />
                    Pickup window: <b>{pickupWindowText}</b>.
                  </Typography>
                </Paper>
              )}

              <CategoryTabs
                categories={menuCategories}
                value={activeCategory}
                onChange={(_, v) => setActiveCategory(v)}
              />

              <Box mt={1.5}>
                <TextField
                  placeholder="Search dishes…"
                  fullWidth
                  size="medium"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 999,
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </Box>
            </Paper>

            {loading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6 }}>
                    <Paper sx={{ p: 1.5, borderRadius: 3 }}>
                      <Skeleton
                        variant="rectangular"
                        height={160}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton sx={{ mt: 1 }} width="60%" />
                      <Skeleton width="90%" />
                      <Skeleton width="40%" />
                      <Skeleton
                        variant="rectangular"
                        height={36}
                        sx={{ mt: 1, borderRadius: 2 }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : filteredItems.length > 0 ? (
              <Grid
                container
                spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
                justifyContent="center"
                alignItems="stretch"
              >
                {filteredItems.map((menuItem) => {
                  const outOfStock = !isActive(menuItem);
                  const isBargain = isBargainBagItem(menuItem);

                  const blockedByCart =
                    (isBargain && cartHasOtherItems) ||
                    (!isBargain && cartHasBargainBag);

                  const blockedByTime = isBargain && !bargainBagAllowedNow;

                  const disabled = outOfStock || blockedByCart || blockedByTime;

                  let disabledReason = "";
                  let disabledLabel = "";
                  let disabledButtonText = "";

                  if (disabled) {
                    if (outOfStock) {
                      disabledLabel = "Out of stock";
                      disabledButtonText = "Out";
                      disabledReason = "Currently unavailable";
                    } else if (blockedByTime) {
                      disabledLabel = "Ends 7:30";
                      disabledButtonText = "Unavailable";
                      disabledReason =
                        "Only available while we’re open and before 7:30 PM";
                    } else if (blockedByCart) {
                      disabledLabel = "Separate";
                      disabledButtonText = "Separate";
                      disabledReason = isBargain
                        ? "Must be purchased alone (remove other items first)"
                        : "Bargain Bag is in your cart (must checkout separately)";
                    }
                  }

                  return (
                    <Grid
                      key={menuItem.menuItemId}
                      size={{ xs: 12, sm: 6, md: 3 }}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <MenuCard
                        item={menuItem}
                        hasOptions={!!menuItem.options?.length}
                        onAdd={(payload) =>
                          handleAddToCartClick(menuItem, payload?.qty ?? 1)
                        }
                        outOfStock={disabled}
                        disabledReason={disabledReason}
                        disabledLabel={disabledLabel}
                        disabledButtonText={disabledButtonText}
                        fixedWidth={350}
                        fixedHeight={340}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper sx={{ p: 3, textAlign: "center", borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  No items found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try a different search, or explore another category above.
                </Typography>
              </Paper>
            )}
          </>
        )}

        <Snackbar
          open={snackOpen}
          autoHideDuration={3200}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)}>
            {snackMsg}
          </Alert>
        </Snackbar>

        <OptionsDialog
          key={selectedItem?.menuItemId || "none"}
          open={pickerOpen}
          onClose={() => {
            setPickerOpen(false);
            setSelectedItem(null);
          }}
          menuItem={selectedItem}
          defaultQty={selectedItem?._qty || 1}
          onConfirm={handleOptionsConfirm}
        />

        <Dialog
          open={bargainModalOpen}
          onClose={handleBargainDecline}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 900 }}>
            🥡 Mea Bargain Bag Rules (Thai Wife Approved 😅)
          </DialogTitle>

          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This is the best way to try new items we’re testing (and end-of-night
              goodies). Picky eaters need not apply 🙃
            </Typography>

            <Divider sx={{ my: 1, opacity: 0.6 }} />

            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  <b>Separate order only:</b> no other menu items can be added with a
                  Mea Bargain Bag.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <b>Pickup window:</b> {pickupWindowText} (Eastern).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <b>No substitutions.</b> Your bag may include new menu items we’re
                  trying out, fresh rolls at the end of the night, or anything else
                  the Thai Wife is feeling.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <b>Picky eaters need not apply.</b> Surprise bag = Thai luck 🇹🇭🍀
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <b>No refunds.</b> If you don’t pick up your order, refunds will not be given.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <b>No refunds if you don’t like the food.</b> We’ll try to improve next time —
                  but you’re paying for the <i>attempt</i> of the bag. Chance of{" "}
                  <b>Thai luck</b> ✨
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  By purchasing a Mea Bargain Bag, you agree to all rules of the bag. 🙏
                </Typography>
              </li>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Pro tip: let the Thai Wife choose — you might find your new favorite 😄
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleBargainDecline} variant="outlined">
              It’s not for me
            </Button>
            <Button
              onClick={handleBargainAccept}
              variant="contained"
              sx={{ fontWeight: 900 }}
            >
              I accept
            </Button>
          </DialogActions>
        </Dialog>

        <LastOrderSnackbar
          open={bannerOpen}
          lastOrderId={lastOrderId}
          onView={viewLastOrder}
          onDismiss={dismissBanner}
        />

        {isOrderingEnabled && showCartButton && cartItems.length > 0 && (
          <FloatingCartButton
            onClick={() => navigate("/checkout")}
            count={cartItems.reduce((sum, i) => sum + (i.qty || 1), 0)}
            icon={<ShoppingCartIcon sx={{ mr: 1 }} />}
          />
        )}
      </Box>
    </ThaiPaperBackground>
  );
}